import { useState, useEffect } from 'react';
import { Menu } from 'lucide-react';
import logo from '../assets/images/logo-Artistto.png';

const Navbar = ({ scrollToFooter }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  
  // Internal scroll to footer function for when prop is not provided
  const handleScrollToFooter = () => {
    if (scrollToFooter) {
      // Use the prop function if provided (for Home page)
      scrollToFooter();
    } else {
      // Default behavior: scroll to bottom of page
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }
  };

  // CHANGED: Toggle mobile menu instead of scrolling to footer
  const handleMobileMenuToggle = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // CHANGED: Close mobile menu function
  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const viewportHeight = window.innerHeight; // This is 100vh

      // Only set isScrolled to true when scrolled past 100vh
      setIsScrolled(scrollTop > viewportHeight * 0.10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isScrolled]); // Added isScrolled to dependency array for console.log

  // NEW: Prevent body scroll when menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      {/* Desktop Navbar */}
      <nav className={`hidden md:flex fixed top-0 left-0 right-0 z-50 justify-between items-center p-4 md:p-6 lg:px-12 transition-all duration-300 ${isScrolled ? 'bg-black/100' : 'bg-transparent'
        }`}>
        <div className="text-2xl md:text-3xl font-bold italic text-white">
          <img src={logo} alt="Artistto" onClick={() => window.location.href = '/'} className="w-[84px] h-[35px] md:w-[120px] md:h-[50px] object-contain" />
        </div>
        <ul className="flex gap-8 text-base py-2.5 px-4">
          <li><a href="https://chat.whatsapp.com/BFxWm1sdDXJD5DFK7uF57h" className="text-white hover:text-orange-400 transition-colors duration-300">Community</a></li>
          <li><a href="#audio-video" onClick={() => window.location.href = '/audio-video-production'} className="text-white hover:text-orange-400 transition-colors duration-300">Audio & Video</a></li>
          <li><a href="#marketing" onClick={() => window.location.href = '/music-marketing'} className="text-white hover:text-orange-400 transition-colors duration-300">Music Marketing</a></li>
          <li><a href="#about-us" onClick={() => window.location.href = '/about-us'} className="text-white hover:text-orange-400 transition-colors duration-300">About Us</a></li>
        </ul>
        <button className="text-sm md:text-[16px] bg-[#FF8000] text-white py-[12px] px-[24px] rounded-xl font-normal hover:shadow-lg hover:shadow-orange-500/30 hover:scale-105 transition-all duration-300" onClick={() => window.location.href = '/contact'}>
          Contact
        </button>
      </nav>

      {/* Mobile Navbar - sticky with black background on scroll */}
      <nav className={`flex md:hidden fixed top-0 left-0 right-0 z-50 justify-between items-center px-4 py-3 transition-all duration-300 ${isScrolled ? 'bg-black/100' : 'bg-transparent'}`}>
        <img src={logo} alt="Artistto" className="w-[84px] h-[35px] object-contain" onClick={() => window.location.href = '/'} />
        {/* CHANGED: Menu button now toggles mobile menu instead of scrolling */}
        <button className="text-white" onClick={handleMobileMenuToggle}>
          {isMobileMenuOpen ? (
            // X icon when menu is open
            <div className="w-6 h-6 flex items-center justify-center">
              <span className="text-2xl font-light">×</span>
            </div>
          ) : (
            // Hamburger menu when closed
            <Menu size={24} />
          )}
        </button>
      </nav>

      {/* CHANGED: Partial Overlay Mobile Menu with Glass Effect */}
      <div className={`fixed inset-0 z-[60] md:hidden transition-all duration-300 ${isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}>
        {/* Background overlay - clickable to close */}
        <div 
          className="absolute inset-0 bg-black/30"
          onClick={closeMobileMenu}
        ></div>
        
        {/* Menu Content - Full width, 45% height */}
        <div className={`absolute top-0 left-0  w-full bg-black/20 backdrop-blur-xl border-b border-white/10 transition-transform duration-300 ${isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'}`}>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-6 border-b border-white/10">
            <img src={logo} alt="Artistto" className="w-[84px] h-[35px] object-contain" onClick={() => window.location.href = '/'}/>
            <button
              onClick={closeMobileMenu}
              className="w-6 h-6 flex items-center justify-center text-white text-2xl"
            >
              ×
            </button>
          </div>

          {/* Menu Items */}
          <div className="flex flex-col flex-1 px-6 py-6 space-y-4">
            <button
              onClick={() => {
                window.location.href = 'https://chat.whatsapp.com/BFxWm1sdDXJD5DFK7uF57h';
                closeMobileMenu();
              }}
              className="w-full text-left text-white text-lg font-medium py-4 border-b border-white/10 hover:text-orange-400 transition-colors"
            >
              Community
            </button>
            
            <button
              onClick={() => {
                window.location.href = '/audio-video-production';
                closeMobileMenu();
              }}
              className="w-full text-left text-white text-lg font-medium py-4 border-b border-white/10 hover:text-orange-400 transition-colors"
            >
              Audio & Video Production
            </button>
            
            <button
              onClick={() => {
                window.location.href = '/music-marketing';
                closeMobileMenu();
              }}
              className="w-full text-left text-white text-lg font-medium py-4 border-b border-white/10 hover:text-orange-400 transition-colors"
            >
              Music Marketing
            </button>

            <button
              onClick={() => {
                window.location.href = '/about-us';
                closeMobileMenu();
              }}
              className="w-full text-left text-white text-lg font-medium py-4 border-b border-white/10 hover:text-orange-400 transition-colors"
            >
              About Us
            </button>

            <button
              onClick={() => {
                window.location.href = '/contact';
                closeMobileMenu();
              }}
              className="w-full text-left text-white text-lg font-medium py-4 border-b border-white/10 hover:text-orange-400 transition-colors"
            >
              Contact Us
            </button>
            
            {/* <button
              onClick={() => {
                handleScrollToFooter();
                closeMobileMenu();
              }}
              className="w-full text-left text-white text-lg font-medium py-4 border-b border-white/10 hover:text-orange-400 transition-colors"
            >
              About Us
            </button> */}
          </div>

          {/* Join Community Button */}
          {/* <div className="mt-auto px-6 pb-6">
            <button
              onClick={() => {
                window.location.href = 'https://chat.whatsapp.com/BFxWm1sdDXJD5DFK7uF57h';
                closeMobileMenu();
              }}
              className="w-full bg-[#FF8000] text-white text-lg font-medium py-3 rounded-xl hover:bg-orange-600 transition-colors"
            >
              Join Community
            </button>
          </div> */}
        </div>
      </div>
    </>
  );
};

export default Navbar;